import React, { useEffect, useRef, useState } from 'react';
import styles from './Table.module.css';

const isPrimitive = (value) =>
  value == null ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

const EditableCellInput = ({ value, onChange, disabled }) => {
  const [inputValue, setInputValue] = useState(value ?? '');

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  const handleChange = (e) => {
    setInputValue(e.target.value);
    onChange(e.target.value);
  };

  return (
    <input
      className={styles.editableInput}
      value={inputValue}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      disabled={disabled}
    />
  );
};

/**
 * A lightweight, config-driven data table with a grey uppercase header and
 * row dividers. See `PaginatedTable` for pagination/toolbar.
 *
 * Each column declares how it renders via its own `render(row, index)`. When a
 * column has no `render`, the cell falls back to `row[column.id]` — unless
 * `editable` is set, in which case a primitive value renders as an inline
 * input instead. `render`, when present, always wins over `editable`/`actions`
 * — matching the "a fully custom cell short-circuits everything else" rule
 * this component has always followed.
 *
 * @component
 * @param {Object} props
 * @param {Array<{
 *   id: string,
 *   title?: import('react').ReactNode,
 *   width?: string|number,
 *   maxWidth?: string|number,
 *   align?: 'left'|'center'|'right',
 *   strong?: boolean,
 *   sticky?: boolean,
 *   sortable?: boolean,
 *   getSortValue?: (row: any) => any,
 *   editable?: boolean,
 *   onCellChange?: (row: any, newValue: any) => void,
 *   actions?: (row: any) => import('react').ReactNode,
 *   render?: (row: any, index: number) => import('react').ReactNode,
 * }>} props.columns - Column definitions. `strong` renders that column's cells in a bold, darker style; `sticky` freezes the column to the left edge (must be a contiguous run starting at the first column, same requirement as `stickyHeader`'s scroll-container note). `sortable` makes the header clickable/keyboard-operable to toggle asc/desc, sorting by `getSortValue(row)` (defaults to `row[id]`) — **when any column is `sortable`, pass `getRowKey` too**: without a stable key, sorting can silently reattach a row's local component state (e.g. a spinner mid-flight) to the wrong row after reordering. `editable` + `onCellChange` render an inline input for primitive values when `render` is absent; a non-primitive value with `editable` logs a warning and falls back to a plain cell. `actions` renders extra content alongside the default value (ignored while a cell is rendering as an editable input, and irrelevant once `render` is set — `render` fully replaces the cell).
 * @param {any[]} [props.data] - The rows to render. Ignored if `sections` is provided.
 * @param {Array<{ title?: import('react').ReactNode, rows: any[] }>} [props.sections] - Renders grouped rows with an optional full-width sub-header per group, instead of a flat `data` list. **Takes precedence over `data` when both are passed.** Sorting (via a sortable column) is applied independently within each section's own rows — sections never interleave, since the grouping is assumed to be meaningful to the caller.
 * @param {(column: any) => import('react').ReactNode} [props.renderHeaderCell] - Optional custom header-cell renderer, receives the whole column object.
 * @param {(row: any, index: number) => string|number} [props.getRowKey] - Returns a stable React key for a row (defaults to a running render-order index — fine for static tables, but see the `sortable` note above for why this should be set on any table that reorders or highlights rows).
 * @param {(row: any, index: number) => string|undefined} [props.getRowClassName] - Returns an extra class for a row's `<tr>` (e.g. a disconnected tint). Note sticky-column cells paint their own opaque background, so a row-state background must target the row's `td`s (with enough specificity to beat `td.dataTableColSticky`'s white), not just the `<tr>`.
 * @param {import('react').ReactNode} [props.emptyMessage] - Shown as a single spanning row when there is no data. This component does not supply default text — pass your own (localized) message.
 * @param {string} [props.className] - Optional class applied to the `<table>` element.
 * @param {boolean} [props.stickyHeader] - When true, the header row sticks to the top of the nearest scrolling ancestor as the body scrolls past it. That ancestor must actually establish a scroll container — `stickyHeader` has no effect if nothing around the table scrolls.
 * @param {boolean} [props.rowDividers=true] - Whether body rows get a bottom border. Set to `false` for a borderless look (e.g. when rows are visually separated some other way).
 * @param {boolean} [props.loading] - Shows a thin indeterminate progress bar above the table (same visual language as `PaginatedTable`'s loading state). Passing this prop (even `false`) opts into the wrapping markup needed for the bar.
 * @param {string|number} [props.highlightedRowId] - Row whose `getRowKey` result matches this value gets a highlighted background.
 * @param {{ current: Record<string|number, HTMLTableRowElement> }} [props.rowRefs] - A `useRef({})`-shaped object. Each row's DOM node is written to `rowRefs.current[rowKey]` via callback ref — useful for the caller to scroll a specific row into view. Not cleaned up on unmount, matching this component's other ref-passing props.
 * @param {(row: any) => void} [props.onRowDoubleClick] - Called when a row is double-clicked. Safely ignored if not a function. Adds a pointer cursor to the row.
 */
const Table = ({
  columns = [],
  data = [],
  sections,
  renderHeaderCell,
  getRowKey,
  getRowClassName,
  onRowDoubleClick,
  emptyMessage,
  className,
  stickyHeader,
  rowDividers = true,
  loading,
  highlightedRowId,
  rowRefs,
  ...rest
}) => {
  const [sortState, setSortState] = useState({ columnId: null, direction: 'asc' });
  const [sortSpins, setSortSpins] = useState(0);
  const warnedMissingRowKeyRef = useRef(false);
  const warnedNonPrimitiveEditRef = useRef(false);

  const hasSortableColumn = columns.some((column) => column.sortable);
  if (hasSortableColumn && !getRowKey && !warnedMissingRowKeyRef.current) {
    warnedMissingRowKeyRef.current = true;
    console.error(
      'Table: `getRowKey` should be provided when any column is `sortable` — without it, row identity is index-based and local state inside row cells can silently attach to the wrong row after sorting.'
    );
  }

  const effectiveSections = sections ?? [{ rows: data }];

  const handleSort = (columnId) => {
    const isSameColumn = sortState.columnId === columnId;
    setSortState({
      columnId,
      direction: isSameColumn && sortState.direction === 'asc' ? 'desc' : 'asc',
    });
    setSortSpins((prev) => (isSameColumn ? prev + 1 : 0));
  };

  const sortRows = (rows) => {
    if (!sortState.columnId) {
      return rows;
    }
    const column = columns.find((c) => c.id === sortState.columnId);
    if (!column) {
      return rows;
    }
    const getSortValue = column.getSortValue || ((row) => row[column.id]);
    return [...rows].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      if (aValue < bValue) {
        return sortState.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortState.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const toCssLength = (width) => (typeof width === 'number' ? `${width}px` : width ?? '0px');
  const stickyLeftOffsets = columns.map((_, index) => {
    const priorWidths = columns.slice(0, index).map((c) => toCssLength(c.width));
    return priorWidths.length ? `calc(${priorWidths.join(' + ')})` : '0px';
  });

  const totalRowCount = effectiveSections.reduce((sum, section) => sum + section.rows.length, 0);

  const renderCellContent = (column, row, index) => {
    if (column.render) {
      return column.render(row, index);
    }

    const rawValue = row[column.id];
    const canEditInline = column.editable && isPrimitive(rawValue);

    if (column.editable && !isPrimitive(rawValue) && !warnedNonPrimitiveEditRef.current) {
      warnedNonPrimitiveEditRef.current = true;
      console.error(
        'Table: only primitive values can be edited inline — use `render` to customize this cell instead.'
      );
    }

    if (canEditInline) {
      return (
        <EditableCellInput
          value={rawValue}
          onChange={(newValue) => column.onCellChange?.(row, newValue)}
        />
      );
    }

    return (
      <div className={styles.cellContent}>
        <span className={styles.cellValue}>{rawValue}</span>
        {column.actions && column.actions(row)}
      </div>
    );
  };

  let rowCounter = 0;

  const table = (
    <table
      className={[styles.dataTable, !rowDividers && styles.dataTableNoDividers, className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <thead>
        <tr>
          {columns.map((column, index) => {
            const isSorted = sortState.columnId === column.id;
            const headerLabel = renderHeaderCell ? renderHeaderCell(column) : column.title;

            return (
              <th
                key={column.id}
                aria-sort={
                  column.sortable ? (isSorted ? (sortState.direction === 'asc' ? 'ascending' : 'descending') : 'none') : undefined
                }
                className={[
                  styles.dataTableHeadCell,
                  column.sortable && styles.dataTableHeadCellSortable,
                  stickyHeader && styles.dataTableHeadCellSticky,
                  column.sticky && styles.dataTableColSticky,
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  ...(column.width != null ? { width: column.width } : {}),
                  ...(column.maxWidth != null ? { maxWidth: column.maxWidth } : {}),
                  ...(column.align ? { textAlign: column.align } : {}),
                  ...(column.sticky ? { position: 'sticky', left: stickyLeftOffsets[index] } : {}),
                }}
              >
                {column.sortable ? (
                  <button
                    type="button"
                    className={styles.sortButton}
                    onClick={() => handleSort(column.id)}
                  >
                    {headerLabel}
                    <span
                      className={[styles.sortIndicator, isSorted && styles.sortIndicatorActive]
                        .filter(Boolean)
                        .join(' ')}
                      aria-hidden="true"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 10 10"
                        fill="none"
                        style={{
                          transform: `rotate(${(isSorted ? sortSpins : 0) * -180}deg)`,
                        }}
                      >
                        <path
                          d="M5 8.5V1.5M2 4.5L5 1.5L8 4.5"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                ) : (
                  headerLabel
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {totalRowCount === 0 && emptyMessage != null ? (
          <tr>
            <td className={styles.dataTableEmpty} colSpan={columns.length}>
              {emptyMessage}
            </td>
          </tr>
        ) : (
          effectiveSections.map((section, sectionIndex) => (
            <React.Fragment key={section.title ?? `section_${sectionIndex}`}>
              {section.title && (
                <tr className={styles.dataTableSectionRow}>
                  <td className={styles.dataTableSectionCell} colSpan={columns.length}>
                    {section.title}
                  </td>
                </tr>
              )}
              {sortRows(section.rows).map((row) => {
                const rowKey = getRowKey ? getRowKey(row, rowCounter) : rowCounter;
                const rowIndex = rowCounter;
                rowCounter += 1;
                const isHighlighted = highlightedRowId != null && rowKey === highlightedRowId;

                return (
                  <tr
                    key={rowKey}
                    ref={(el) => {
                      if (rowRefs && el) {
                        rowRefs.current[rowKey] = el;
                      }
                    }}
                    className={[
                      styles.dataTableRow,
                      isHighlighted && styles.dataTableRowHighlighted,
                      typeof onRowDoubleClick === 'function' && styles.dataTableRowClickable,
                      getRowClassName && getRowClassName(row, rowIndex),
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onDoubleClick={() => {
                      if (typeof onRowDoubleClick === 'function') {
                        onRowDoubleClick(row);
                      }
                    }}
                  >
                    {columns.map((column, colIndex) => (
                      <td
                        key={column.id}
                        className={[
                          styles.dataTableCell,
                          column.strong && styles.dataTableCellStrong,
                          column.sticky && styles.dataTableColSticky,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={{
                          ...(column.align ? { textAlign: column.align } : {}),
                          ...(column.sticky
                            ? { position: 'sticky', left: stickyLeftOffsets[colIndex] }
                            : {}),
                        }}
                      >
                        {renderCellContent(column, row, rowIndex)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </React.Fragment>
          ))
        )}
      </tbody>
    </table>
  );

  if (loading === undefined) {
    return table;
  }

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.progressBarTrack}>
        {loading && <div className={styles.progressBarFill} />}
      </div>
      {table}
    </div>
  );
};

export default Table;
